/*
  PEANUT. scale and edge layer, the two things in it that need a script.
  Added 2026-10-11 with assets/peanut-edge.css.

  1. The drizzle line over the wordmark draws itself as the page scrolls.
  2. The desktop buy bar on the product page shows once Add to cart has
     scrolled away.
  3. The notes on the bottle drawing arrive in order as it scrolls into view.

  All three are progressive. With this file absent the line is simply there,
  whole, the notes are all there from the start, and the bar never appears:
  the page loses nothing it needs.

  Loaded by sections/peanut-wordmark.liquid, sections/peanut-bottle.liquid and
  the product section, so a page can ask for it more than once. The guard
  makes every one after the first a no-op.
*/
(function () {
  'use strict';

  if (window.peanutEdgeInit) return;
  window.peanutEdgeInit = true;

  if (!('IntersectionObserver' in window)) return;

  /* ------------------------------------------------------------------------
     1. The line over the wordmark.

     peanut-edge.css reads --draw, 0 to 1, as how much of the path is stroked.
     It is tied to the scroll position rather than played once on entry, so
     the line follows the hand: scroll back up and it lifts off again.

     Progress starts when the top of the mark is nine tenths of the way down
     the screen and ends when the mark's middle is a little above the screen's
     middle, which is where the eye is when the whole word is first in view.

     data-live is what switches the dash on. It is only ever set here, and
     not at all under reduced motion, so in both of those cases the CSS shows
     the finished line.

     The scroll listener only exists while a mark is on screen, and it does
     one measurement per frame at most.

     --draw is written on the path, not on the mark. A custom property set on
     a parent restyles every element under it on each write; set on the one
     element that reads it, a new value touches that element alone.
     ------------------------------------------------------------------------ */
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var marks = document.querySelectorAll('[data-peanut-mark]');

  if (marks.length && !reduce.matches) {
    var onScreen = [];
    var queued = false;
    var listening = false;

    function measure() {
      queued = false;
      var vh = window.innerHeight;
      onScreen.forEach(function (mark) {
        var rect = mark.getBoundingClientRect();
        var start = vh * 0.9;
        var end = vh * 0.42 - rect.height / 2;
        var progress = (start - rect.top) / (start - end);
        progress = Math.max(0, Math.min(1, progress));
        mark.peanutLine.style.setProperty('--draw', progress.toFixed(4));
      });
    }

    function request() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    }

    var markObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var at = onScreen.indexOf(entry.target);
          if (entry.isIntersecting && at === -1) onScreen.push(entry.target);
          if (!entry.isIntersecting && at !== -1) onScreen.splice(at, 1);
        });
        if (onScreen.length && !listening) {
          window.addEventListener('scroll', request, { passive: true });
          window.addEventListener('resize', request);
          listening = true;
        } else if (!onScreen.length && listening) {
          window.removeEventListener('scroll', request);
          window.removeEventListener('resize', request);
          listening = false;
        }
        request();
      },
      { rootMargin: '15% 0px 15% 0px' }
    );

    marks.forEach(function (mark) {
      var line = mark.querySelector('.peanut-mark__drizzle path');
      if (!line) return;
      mark.peanutLine = line;
      /* A mark that is already above the screen when the page opens (a deep
         link to the FAQ, a reload half way down) starts finished. */
      if (mark.getBoundingClientRect().bottom < 0) line.style.setProperty('--draw', '1');
      mark.setAttribute('data-live', '');
      markObserver.observe(mark);
    });
  }

  /* ------------------------------------------------------------------------
     2. The desktop buy bar.

     Shown when the inline Add to cart has gone off the top of the screen,
     and only from 1024px, where peanut-edge.css gives it a layout. Phones
     have their own bar at the bottom, driven by peanut-sticky-cta.js, and
     the two never show together because their widths do not overlap.

     Hidden means visibility: hidden in the CSS, so the button inside leaves
     the tab order, and aria-hidden follows for the same reason.
     ------------------------------------------------------------------------ */
  var bar = document.querySelector('[data-peanut-buybar]');
  var anchor = document.querySelector('[data-peanut-product] [data-peanut-cta-anchor]');

  if (bar && anchor) {
    var wide = window.matchMedia('(min-width: 1024px)');
    var passed = false;

    function show() {
      var on = passed && wide.matches;
      bar.setAttribute('data-visible', on ? 'true' : 'false');
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        });
        show();
      },
      { threshold: 0 }
    ).observe(anchor);

    wide.addEventListener('change', show);
    show();
  }

  /* ------------------------------------------------------------------------
     3. The bottle's notes arrive in order.

     peanut-edge.css does the motion: data-arm puts the leader lines, the pins
     and the notes in their starting state, data-seen lets them go, each one
     70ms behind the last. This only decides when.

     Armed only when the drawing is still below the screen. On a reload half
     way down the page, or a link straight to this section, it is already in
     view, and hiding it in order to reveal it would be a flicker. Never armed
     under reduced motion. So the attribute that hides anything is only ever
     set in the one case where the reveal will be seen.

     Once. The observer lets go of the drawing as soon as it has been seen:
     notes that hid themselves again on every pass would be the page fighting
     its reader. It fires when the top of the drawing is a quarter of the
     screen up from the bottom edge, so the first note is already in view when
     it starts.
     ------------------------------------------------------------------------ */
  var bottles = document.querySelectorAll('[data-peanut-bottle]');

  if (bottles.length && !reduce.matches) {
    var bottleObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var bottle = entry.target.closest('[data-peanut-bottle]');
          if (bottle) bottle.setAttribute('data-seen', '');
          bottleObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -25% 0px', threshold: 0 }
    );

    bottles.forEach(function (bottle) {
      var stage = bottle.querySelector('.peanut-bottle__stage');
      if (!stage || stage.getBoundingClientRect().top < window.innerHeight) return;
      bottle.setAttribute('data-arm', '');
      bottleObserver.observe(stage);
    });
  }
})();
